/**
 * Modern Embla Product Gallery - WITH MATERIAL FILTERING + FRAME OVERLAY + VIDEO + JSON VARIANT SYNC + HAPTIC GLOSS
 */

(function () {
  'use strict';

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log(...args);
  const warn = (...args) => DEBUG && console.warn(...args);

  const ALL_MATERIAL_SUFFIXES = [
    '-leinwand.', '-detail-leinwand.',
    '-acryl.', '-detail-acryl.',
    '-brushed.', '-detail-brushed.',
    '-alu.', '-detail-alu.',
  ];

  const MATERIAL_SUFFIX_MAP = {
    leinwand: ['-leinwand.', '-detail-leinwand.'],
    leinwandbild: ['-leinwand.', '-detail-leinwand.'],
    keilrahmen: ['-leinwand.', '-detail-leinwand.'],
    'alu-geburstet': ['-brushed.', '-detail-brushed.'],
    acrylglas: ['-acryl.', '-detail-acryl.'],
    'acryl-glas': ['-acryl.', '-detail-acryl.'],
    'gallery-print': ['-acryl.', '-detail-acryl.'],
    'alu-weiss': ['-alu.', '-detail-alu.'],
    'alu-weiß': ['-alu.', '-detail-alu.'],
  };

  function waitForEmbla(triesLeft = 100) {
    if (typeof EmblaCarousel === 'undefined') {
      if (triesLeft <= 0) { console.error('Embla Carousel not available'); return; }
      setTimeout(() => waitForEmbla(triesLeft - 1), 100);
      return;
    }
    initializeGalleries();
  }

  function initializeGalleries(root = document) {
    root.querySelectorAll('[data-embla-gallery]').forEach((gallery) => {
      if (gallery._emblaGallery) return;
      new ProductGallery(gallery);
    });
  }

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainSlider = container.querySelector('[data-embla-main]');
      if (!this.mainSlider) return;

      this.viewport = this.mainSlider.querySelector('.embla-product__viewport');
      if (!this.viewport) return;

      this.slideContainer = this.viewport.querySelector('.embla-product__container');
      if (!this.slideContainer) return;

      this.prevBtn = this.mainSlider.querySelector('[data-embla-prev]');
      this.nextBtn = this.mainSlider.querySelector('[data-embla-next]');
      this.dotsContainer = this.mainSlider.querySelector('[data-embla-dots]');

      this.thumbsContainer = container.querySelector('[data-product-thumbs]');
      this.thumbScroller = container.querySelector('[data-thumb-scroller]');
      this.thumbPrevBtn = container.querySelector('[data-thumb-prev]');
      this.thumbNextBtn = container.querySelector('[data-thumb-next]');

      this.hapticOverlay = container.querySelector('[data-haptic-overlay]'); // Neu für HapticArt

      this.currentIndex = 0;
      this.hoveredIndex = null;
      this.isDragging = false;
      this.hasDragged = false;
      this.startX = 0;
      this.startY = 0;
      this.scrollLeft = 0;
      this.scrollTop = 0;

      this.allSlides = Array.from(this.slideContainer.querySelectorAll('.embla-product__slide'));
      this.allThumbItems = Array.from(this.thumbScroller?.querySelectorAll('.product__thumb-item') || []);
      this.currentMaterial = null;
      this.sectionRoot = container.closest('.shopify-section') || document;

      // Varianten-Daten laden und Event-Listener setzen
      this.parseVariantJson();

      this.initMainSlider();
      this.initThumbnails();
      this.initThumbDragScroll();
      this.preventImageDrag();
      this.initMaterialFilter();
      this.initFrameOverlay();
      this.initVariantSync();

      this.container._emblaGallery = this;
      log('ProductGallery initialized');
    }

    /* =========================================
       FRAME OVERLAY
       ========================================= */

    initFrameOverlay() {
      this.frameOverlay = this.container.querySelector('[data-frame-overlay]');
      if (!this.frameOverlay) return;

      this.currentFrameValue = null;

      // --- NEU: PRELOADER (Lädt die Rahmen im Hintergrund in den Cache) ---
      const preloadUrls = [
        this.frameOverlay.dataset.baseUrlSchwarz,
        this.frameOverlay.dataset.baseUrlWeiss,
        this.frameOverlay.dataset.baseUrlSilber,
        this.frameOverlay.dataset.baseUrlGold
      ];
      preloadUrls.forEach(url => {
        if (url && url.trim() !== '') {
          const img = new Image();
          img.src = url;
        }
      });
      // ------------------------------------------------------------------

      const isGalerieabzug = this.frameOverlay.dataset.isGalerieabzug === 'true';
      const frameOptionIndex = isGalerieabzug ? 'option2' : 'option3';

      const frameFieldset = this.sectionRoot.querySelector(`fieldset[data-index="${frameOptionIndex}"]`);
      
      if (!frameFieldset) { 
        if (this.frameOverlay) {
          this.frameOverlay.remove(); 
          this.frameOverlay = null;   
        }
        return; 
      }

      const frameInputs = Array.from(frameFieldset.querySelectorAll('input'));
      if (!frameInputs.length) return;

      frameFieldset.addEventListener('change', (e) => {
        const input = e.target.closest('input');
        if (input && input.checked) {
          const val = input.dataset.frameKey || input.value;
          this.updateFrameOverlay(val);
        }
      });

      const checked = frameInputs.find((i) => i.checked);
      if (checked) {
        const val = checked.dataset.frameKey || checked.value;
        this.updateFrameOverlay(val);
      }
    }

    updateFrameOverlay(value) {
      if (!this.frameOverlay) return;
      const v = value.trim().toLowerCase();

      if (v === this.currentFrameValue) return;
      this.currentFrameValue = v;

      const isNone   = ['ohne', 'without', ''].includes(v);
      const isBlack  = ['schwarz', 'black'].includes(v);
      const isWhite  = ['weiss', 'weiß', 'white'].includes(v);
      const isSilver = ['silber', 'silver'].includes(v);
      const isGold   = ['gold'].includes(v);

      if (isNone) {
        this.frameOverlay.style.display = 'none';
        this.frameOverlay.classList.remove('is-visible');
        this.frameOverlay.src = '';
        return;
      }

      let url = '';
      if (isBlack)       url = this.frameOverlay.dataset.baseUrlSchwarz || '';
      else if (isWhite)  url = this.frameOverlay.dataset.baseUrlWeiss || '';
      else if (isSilver) url = this.frameOverlay.dataset.baseUrlSilber || '';
      else if (isGold)   url = this.frameOverlay.dataset.baseUrlGold || '';

      if (url) {
        this.frameOverlay.src = url;
        this.frameOverlay.style.display = '';
        this.frameOverlay.classList.add('is-visible');
      } else {
        this.frameOverlay.style.display = 'none';
        this.frameOverlay.classList.remove('is-visible');
      }
    }

    reapplyFrameOverlay() {
      if (!this.frameOverlay) return;
      
      const isGalerieabzug = this.frameOverlay.dataset.isGalerieabzug === 'true';
      const frameOptionIndex = isGalerieabzug ? 'option2' : 'option3';

      const frameFieldset = this.sectionRoot.querySelector(`fieldset[data-index="${frameOptionIndex}"]`);
      if (!frameFieldset) return;

      const frameInputs = Array.from(frameFieldset.querySelectorAll('input'));
      const checked = frameInputs.find((i) => i.checked);
      
      if (checked) {
        const val = checked.dataset.frameKey || checked.value;
        this.updateFrameOverlay(val);
      }
    }

    /* =========================================
       INDEX MAPPING
       ========================================= */

    getEmblaIndexFromOriginal(originalIndex) {
      const visible = Array.from(this.slideContainer.children);
      const target = this.allSlides[originalIndex];
      return target ? visible.indexOf(target) : -1;
    }

    getOriginalIndexFromEmbla(emblaIndex) {
      const visible = Array.from(this.slideContainer.children);
      const target = visible[emblaIndex];
      return target ? this.allSlides.indexOf(target) : -1;
    }

    /* =========================================
       MATERIAL FILTER
       ========================================= */

    initMaterialFilter() {
      const cbs = this.sectionRoot.querySelectorAll('fieldset[data-index="option1"] input');
      if (!cbs.length) return;

      cbs.forEach((cb) => {
        cb.addEventListener('change', () => {
          if (cb.checked) {
            const key = cb.dataset.materialKey || this.cleanSlug(cb.value);
            this.filterByMaterial(key);
          }
        });
      });

      const sel = Array.from(cbs).find((c) => c.checked);
      if (sel) {
        const key = sel.dataset.materialKey || this.cleanSlug(sel.value);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => { this.filterByMaterial(key); }, { timeout: 1000 });
        } else {
          setTimeout(() => this.filterByMaterial(key), 600);
        }
      }
    }

    cleanSlug(value) {
      if (!value) return value;
      return String(value).trim().toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
        .replace(/-+/g, '-').replace(/^-+|-+$/g, '');
    }

    getMaterialSuffixes(material) { return MATERIAL_SUFFIX_MAP[material] || null; }

    slideMatchesMaterial(slide, suffixes) {
      const src = slide.getAttribute('data-image-src') || '';
      const has = ALL_MATERIAL_SUFFIXES.some((s) => src.includes(s));
      if (!has) return true;
      if (suffixes) return suffixes.some((s) => src.includes(s));
      return false;
    }

    filterByMaterial(material) {
      this.currentMaterial = material;
      const suffixes = this.getMaterialSuffixes(material);
      const visSlides = [], visThumbs = [];

      // Temporär entfernen, damit sie nicht gelöscht werden
      if (this.frameOverlay && this.frameOverlay.parentNode) {
        this.frameOverlay.parentNode.removeChild(this.frameOverlay);
      }
      if (this.hapticOverlay && this.hapticOverlay.parentNode) {
        this.hapticOverlay.parentNode.removeChild(this.hapticOverlay);
      }

      for (let i = 0; i < this.allSlides.length; i++) {
        const keep = this.slideMatchesMaterial(this.allSlides[i], suffixes);
        if (keep) visSlides.push(this.allSlides[i]);
        if (this.allThumbItems[i] && keep) visThumbs.push(this.allThumbItems[i]);
      }

      const frag1 = document.createDocumentFragment();
      visSlides.forEach((s) => frag1.appendChild(s));
      this.slideContainer.textContent = '';
      this.slideContainer.appendChild(frag1);

      if (this.thumbScroller) {
        const frag2 = document.createDocumentFragment();
        visThumbs.forEach((t) => {
          t.querySelectorAll('img').forEach(img => img.setAttribute('loading', 'eager'));
          frag2.appendChild(t);
        });
        this.thumbScroller.textContent = '';
        this.thumbScroller.appendChild(frag2);
      }

      // Overlays zum neuen ersten Slide hinzufügen
      if (this.frameOverlay && visSlides.length > 0) {
        visSlides[0].appendChild(this.frameOverlay);
        this.reapplyFrameOverlay();
      }
      if (this.hapticOverlay && visSlides.length > 0) {
        visSlides[0].appendChild(this.hapticOverlay);
      }

      this.emblaApi.reInit();
      setTimeout(() => {
        this.createDots();
        this.updateUI();
        this.updateThumbScrollFade();
        this.updateThumbArrows();
        this.pauseAllVideos();
      }, 50);
    }

    /* =========================================
       VARIANT CHANGE HANDLING (JSON DIRECT)
       ========================================= */

    parseVariantJson() {
      const jsonEl = this.sectionRoot.querySelector('[data-variant-json]');
      this.productVariants = [];
      if (jsonEl) {
        try {
          this.productVariants = JSON.parse(jsonEl.innerHTML || jsonEl.textContent);
        } catch (e) {
          log('Error parsing variant JSON', e);
        }
      }
    }

    initVariantSync() {
      const inputs = this.sectionRoot.querySelectorAll('fieldset.variant-input-wrap input[type="radio"]');
      inputs.forEach(input => {
        input.addEventListener('change', () => {
          setTimeout(() => this.syncVariantImage(), 100);
        });
      });
    }

    syncVariantImage() {
      if (!this.productVariants || !this.productVariants.length) return;

      const opt1 = this.sectionRoot.querySelector('fieldset[data-index="option1"] input:checked')?.value;
      const opt2 = this.sectionRoot.querySelector('fieldset[data-index="option2"] input:checked')?.value;
      const opt3 = this.sectionRoot.querySelector('fieldset[data-index="option3"] input:checked')?.value;

      const matchedVariant = this.productVariants.find(v => {
        return (v.option1 === opt1 || (!v.option1 && !opt1)) &&
               (v.option2 === opt2 || (!v.option2 && !opt2)) &&
               (v.option3 === opt3 || (!v.option3 && !opt3));
      });

      if (matchedVariant && matchedVariant.featured_image) {
        const fullUrl = matchedVariant.featured_image.src.split('?')[0];
        const filename = fullUrl.substring(fullUrl.lastIndexOf('/') + 1);

        const slides = Array.from(this.slideContainer.querySelectorAll('.embla-product__slide'));
        const targetIndex = slides.findIndex(slide => {
           const src = slide.getAttribute('data-image-src') || '';
           return src.includes(filename);
        });

        if (targetIndex !== -1) {
          this.emblaApi.scrollTo(targetIndex);
          this.currentIndex = targetIndex;
          this.updateUI();
        }
      }
    }

    /* =========================================
       MAIN SLIDER
       ========================================= */

    initMainSlider() {
      this.emblaApi = EmblaCarousel(this.viewport, {
        align: 'start', loop: true, skipSnaps: false,
        draggable: true, dragFree: false,
        containScroll: false, speed: 10,
      });

      this.emblaApi.on('select', this.onSlideChange.bind(this));
      this.emblaApi.on('init', this.onInit.bind(this));
      this.emblaApi.on('reInit', this.updateUI.bind(this));

      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener('click', () => this.emblaApi.scrollPrev());
        this.nextBtn.addEventListener('click', () => this.emblaApi.scrollNext());
        this.updateArrows();
        this.emblaApi.on('select', this.updateArrows.bind(this));
      }

      if (this.dotsContainer) {
        this.createDots();
        this.emblaApi.on('select', this.updateDots.bind(this));
      }

      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.emblaApi.scrollPrev();
        if (e.key === 'ArrowRight') this.emblaApi.scrollNext();
      });
    }

    preventImageDrag() {
      if (!this.thumbScroller) return;
      this.thumbScroller.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => { e.preventDefault(); return false; });
        img.addEventListener('contextmenu', (e) => { e.preventDefault(); return false; });
      });
      this.thumbScroller.addEventListener('dragstart', (e) => { e.preventDefault(); return false; });
    }

    /* =========================================
       VIDEO HANDLING
       ========================================= */

    handleSlideVideos() {
      const visibleSlides = Array.from(this.slideContainer.children);
      visibleSlides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === this.currentIndex) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    pauseAllVideos() {
      this.slideContainer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    }

    /* =========================================
       THUMBNAILS
       ========================================= */

    initThumbnails() {
      if (!this.thumbsContainer) return;

      if (this.thumbScroller) {
        this.updateThumbScrollFade();
        this.thumbScroller.addEventListener('scroll', this.updateThumbScrollFade.bind(this));
        window.addEventListener('resize', this.updateThumbScrollFade.bind(this));
      }

      if (this.thumbPrevBtn && this.thumbNextBtn && this.thumbScroller) {
        const isV = this.thumbsContainer?.dataset.position === 'beside';
        this.thumbPrevBtn.addEventListener('click', () => this.scrollThumbs(-200, isV));
        this.thumbNextBtn.addEventListener('click', () => this.scrollThumbs(200, isV));
        this.updateThumbArrows();
        this.thumbScroller.addEventListener('scroll', this.updateThumbArrows.bind(this));
      }

      this.thumbsContainer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-thumb-button]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (this.hasDragged) return;

        const emblaIdx = this.getEmblaIndexFromOriginal(Number(btn.dataset.index));
        if (emblaIdx >= 0) {
          this.currentIndex = emblaIdx;
          this.emblaApi.scrollTo(emblaIdx);
        }
      });

      this.thumbsContainer.addEventListener('pointerenter', (e) => {
        const btn = e.target.closest('[data-thumb-button]');
        if (!btn || this.isDragging) return;
        const idx = this.getEmblaIndexFromOriginal(Number(btn.dataset.index));
        if (idx >= 0) { this.hoveredIndex = idx; this.emblaApi.scrollTo(idx, false); }
      }, true);

      this.thumbsContainer.addEventListener('pointerleave', (e) => {
        const btn = e.target.closest('[data-thumb-button]');
        if (!btn || this.isDragging) return;
        this.hoveredIndex = null;
        this.emblaApi.scrollTo(this.currentIndex, false);
      }, true);
    }

    initThumbDragScroll() {
      if (!this.thumbScroller) return;
      const isV = this.thumbsContainer?.dataset.position === 'beside';

      this.thumbScroller.addEventListener('mousedown', (e) => {
        if (e.target.tagName !== 'A' && e.target.tagName !== 'BUTTON') e.preventDefault();
        this.isDragging = true;
        this.hasDragged = false;
        this.thumbScroller.classList.add('is-dragging');
        this.startX = e.pageX - this.thumbScroller.offsetLeft;
        this.startY = e.pageY - this.thumbScroller.offsetTop;
        this.scrollLeft = this.thumbScroller.scrollLeft;
        this.scrollTop = this.thumbScroller.scrollTop;
      });

      this.thumbScroller.addEventListener('mouseleave', () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.thumbScroller.classList.remove('is-dragging');
        if (this.hasDragged) this.emblaApi.scrollTo(this.currentIndex, false);
        setTimeout(() => { this.hasDragged = false; }, 100);
      });

      this.thumbScroller.addEventListener('mouseup', () => {
        const was = this.isDragging;
        this.isDragging = false;
        this.thumbScroller.classList.remove('is-dragging');
        if (was && this.hasDragged) this.emblaApi.scrollTo(this.currentIndex, false);
        setTimeout(() => { this.hasDragged = false; }, 100);
      });

      this.thumbScroller.addEventListener('mousemove', (e) => {
        if (!this.isDragging) return;
        e.preventDefault();
        if (isV) {
          const walk = (e.pageY - this.thumbScroller.offsetTop) - this.startY;
          if (Math.abs(walk) > 5) this.hasDragged = true;
          this.thumbScroller.scrollTop = this.scrollTop - walk;
        } else {
          const walk = (e.pageX - this.thumbScroller.offsetLeft) - this.startX;
          if (Math.abs(walk) > 5) this.hasDragged = true;
          this.thumbScroller.scrollLeft = this.scrollLeft - walk;
        }
      });

      let tX = 0, tY = 0;
      this.thumbScroller.addEventListener('touchstart', (e) => {
        tX = e.touches[0].pageX; tY = e.touches[0].pageY;
        this.scrollLeft = this.thumbScroller.scrollLeft;
        this.scrollTop = this.thumbScroller.scrollTop;
      }, { passive: true });

      this.thumbScroller.addEventListener('touchmove', (e) => {
        if (isV) {
          this.thumbScroller.scrollTop = this.scrollTop + (tY - e.touches[0].pageY) * 1.5;
        } else {
          this.thumbScroller.scrollLeft = this.scrollLeft + (tX - e.touches[0].pageX) * 1.5;
        }
      }, { passive: true });
    }

    /* =========================================
       THUMB SCROLL HELPERS
       ========================================= */

    updateThumbScrollFade() {
      if (!this.thumbScroller || !this.thumbsContainer) return;
      const isV = this.thumbsContainer.dataset.position === 'beside';
      const pos = isV ? this.thumbScroller.scrollTop : this.thumbScroller.scrollLeft;
      const max = isV
        ? this.thumbScroller.scrollHeight - this.thumbScroller.clientHeight
        : this.thumbScroller.scrollWidth - this.thumbScroller.clientWidth;

      this.thumbsContainer.classList.toggle('scrolled-to-end', pos >= max - 5);
    }

    scrollThumbs(amount, isVertical = false) {
      if (!this.thumbScroller) return;
      this.thumbScroller.scrollBy(isVertical ? { top: amount, behavior: 'smooth' } : { left: amount, behavior: 'smooth' });
    }

    updateThumbArrows() {
      if (!this.thumbScroller || !this.thumbPrevBtn || !this.thumbNextBtn) return;
      const isV = this.thumbsContainer?.dataset.position === 'beside';
      const pos = isV ? this.thumbScroller.scrollTop : this.thumbScroller.scrollLeft;
      const max = isV
        ? this.thumbScroller.scrollHeight - this.thumbScroller.clientHeight
        : this.thumbScroller.scrollWidth - this.thumbScroller.clientWidth;

      this.thumbPrevBtn.classList.toggle('hide', pos <= 0);
      this.thumbNextBtn.classList.toggle('hide', pos >= max - 5);
    }

    /* =========================================
       UI UPDATES
       ========================================= */

    onInit() {
      this.updateUI();
      if (this.thumbScroller) { this.updateThumbScrollFade(); this.updateThumbArrows(); }
      setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    }

    onSlideChange() {
      const prev = this.currentIndex;
      this.currentIndex = this.emblaApi.selectedScrollSnap();
      this.updateActiveThumbnail();
      this.scrollActiveThumbnailIntoView();
      this.dispatchSlideChangeEvent(prev, this.currentIndex);
      this.updateUI();
      this.handleSlideVideos();
    }

    updateUI() {
      this.updateArrows();
      this.updateDots();
      this.updateActiveThumbnail();
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      this.prevBtn.disabled = !this.emblaApi.canScrollPrev();
      this.nextBtn.disabled = !this.emblaApi.canScrollNext();
    }

    createDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.emblaApi.scrollSnapList().forEach((_, i) => {
        const dot = document.createElement('button');
        dot.classList.add('embla-product__dot');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Zu Bild ${i + 1}`);
        dot.addEventListener('click', () => this.emblaApi.scrollTo(i));
        this.dotsContainer.appendChild(dot);
      });
    }

    updateDots() {
      if (!this.dotsContainer) return;
      const sel = this.emblaApi.selectedScrollSnap();
      this.dotsContainer.querySelectorAll('.embla-product__dot').forEach((dot, i) => {
        dot.classList.toggle('embla-product__dot--active', i === sel);
      });
    }

    updateActiveThumbnail() {
      if (!this.thumbsContainer) return;
      const origIdx = this.getOriginalIndexFromEmbla(this.currentIndex);
      this.thumbsContainer.querySelectorAll('[data-thumb-button]').forEach((btn) => {
        const match = Number(btn.dataset.index) === origIdx;
        btn.classList.toggle('is-active', match);
        if (match) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
    }

    scrollActiveThumbnailIntoView() {
      if (!this.thumbScroller || !this.thumbsContainer) return;
      const activeBtn = this.thumbsContainer.querySelector('[data-thumb-button].is-active');
      if (!activeBtn) return;

      const isV = this.thumbsContainer.dataset.position === 'beside';
      const item = activeBtn.closest('.product__thumb-item');
      if (!item) return;

      if (isV) {
        const top = item.offsetTop, h = item.offsetHeight;
        const sH = this.thumbScroller.clientHeight, sT = this.thumbScroller.scrollTop;
        if (top < sT) this.thumbScroller.scrollTo({ top: top - 20, behavior: 'smooth' });
        else if (top + h > sT + sH) this.thumbScroller.scrollTo({ top: top + h - sH + 20, behavior: 'smooth' });
      } else {
        const left = item.offsetLeft, w = item.offsetWidth;
        const sW = this.thumbScroller.clientWidth, sL = this.thumbScroller.scrollLeft;
        if (left < sL) this.thumbScroller.scrollTo({ left: left - 20, behavior: 'smooth' });
        else if (left + w > sL + sW) this.thumbScroller.scrollTo({ left: left + w - sW + 20, behavior: 'smooth' });
      }
    }

    dispatchSlideChangeEvent(prev, curr) {
      this.container.dispatchEvent(new CustomEvent('embla:slideChange', {
        detail: { previousIndex: prev, currentIndex: curr, emblaApi: this.emblaApi },
        bubbles: true,
      }));
    }

    goToSlide(index) { this.emblaApi.scrollTo(index); }
    getCurrentIndex() { return this.currentIndex; }

    destroy() {
      this.pauseAllVideos();
      if (this.emblaApi) this.emblaApi.destroy();
      delete this.container._emblaGallery;
    }
  }

  // Global API
  window.ProductGalleryEmbla = {
    getInstance(container) { return container._emblaGallery; },
    goToSlide(container, index) { this.getInstance(container)?.goToSlide(index); },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForEmbla());
  } else {
    waitForEmbla();
  }

  document.addEventListener('shopify:section:load', (e) => initializeGalleries(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-embla-gallery]').forEach((g) => {
      if (g._emblaGallery) g._emblaGallery.destroy();
    });
  });
})();